import React from 'react';
import { roundedBadgePercentageValue } from './badgePercentageFormatting';

export const BADGE_TOTEM_STAGES = Object.freeze([
  { key: 'potato', label: 'Potato', threshold: 0 },
  { key: 'mouse', label: 'Mouse', threshold: 8 },
  { key: 'raccoon', label: 'Raccoon', threshold: 17 },
  { key: 'cat', label: 'Cat', threshold: 25 },
  { key: 'dog', label: 'Dog', threshold: 33 },
  { key: 'fox', label: 'Fox', threshold: 42 },
  { key: 'donkey', label: 'Donkey', threshold: 50 },
  { key: 'horse', label: 'Horse', threshold: 58 },
  { key: 'wolf', label: 'Wolf', threshold: 67 },
  { key: 'gorilla', label: 'Gorilla', threshold: 75 },
  { key: 'lion', label: 'Lion', threshold: 83 },
  { key: 'eagle', label: 'Eagle', threshold: 92 },
  { key: 'dragon', label: 'Dragon', threshold: 100 },
]);

const PALETTES = Object.freeze({
  potato: { light: '#b98950', mid: '#7b512d', dark: '#322317', accent: '#91a55a' },
  mouse: { light: '#c1c7cd', mid: '#747d86', dark: '#262c32', accent: '#d6ad3b' },
  raccoon: { light: '#b8c0c8', mid: '#69737e', dark: '#181d23', accent: '#d6ad3b' },
  cat: { light: '#c6ccd2', mid: '#76808a', dark: '#232931', accent: '#d6ad3b' },
  dog: { light: '#bda786', mid: '#756044', dark: '#292218', accent: '#d6ad3b' },
  fox: { light: '#d79852', mid: '#8d4d24', dark: '#2b1b14', accent: '#f0c05a' },
  donkey: { light: '#b9bec4', mid: '#6c747c', dark: '#272d33', accent: '#d6ad3b' },
  horse: { light: '#bd9267', mid: '#745036', dark: '#271c16', accent: '#d6ad3b' },
  wolf: { light: '#c3cad1', mid: '#697581', dark: '#1e252d', accent: '#d6ad3b' },
  gorilla: { light: '#d1d7de', mid: '#717c88', dark: '#242b33', accent: '#d6ad3b' },
  lion: { light: '#d8ad52', mid: '#8f612b', dark: '#302014', accent: '#f2d06b' },
  eagle: { light: '#e0e4e8', mid: '#8e98a3', dark: '#252c34', accent: '#d6ad3b' },
  dragon: { light: '#aeb7c2', mid: '#47525e', dark: '#10151b', accent: '#d6ad3b' },
});

export const getBadgeTotemStage = rate => {
  const value = roundedBadgePercentageValue(rate);
  return [...BADGE_TOTEM_STAGES].reverse().find(stage => value >= stage.threshold) || BADGE_TOTEM_STAGES[0];
};

const Shield = ({ accent }) => (
  <>
    <path d="M50 5 L91 23 L86 74 L50 96 L14 74 L9 23 Z" fill="#080a0e" stroke={accent} strokeWidth="2.5" />
    <path d="M50 10 V90 L81 70 L86 28 Z" fill="#151a21" opacity="0.72" />
  </>
);

const EyePair = ({ accent, left = 'M25 43L43 39L39 50L24 51Z', right = 'M75 43L57 39L61 50L76 51Z' }) => (
  <><path d={left} fill={accent} /><path d={right} fill={accent} /></>
);

const Potato = ({ p }) => <>
  <path d="M29 30 C36 17 65 16 75 29 C86 43 79 71 63 81 C47 91 25 79 19 63 C14 49 20 38 29 30 Z" fill={p.mid} stroke={p.dark} strokeWidth="3" />
  <path d="M28 32 C40 24 53 22 64 27 L53 79 C37 80 25 69 22 56 Z" fill={p.light} opacity="0.54" />
  <path d="M55 23 C57 14 64 10 72 8 C68 17 65 23 58 28 M48 23 C43 15 37 13 31 12 C35 20 40 25 47 28" fill="none" stroke={p.accent} strokeWidth="4" strokeLinecap="round" />
  <g fill={p.dark} opacity="0.7"><circle cx="34" cy="45" r="2.4"/><circle cx="61" cy="38" r="2"/><circle cx="68" cy="59" r="2.6"/><circle cx="40" cy="69" r="2"/></g>
</>;

const Mouse = ({ p }) => <>
  <circle cx="29" cy="31" r="12" fill={p.mid} stroke={p.dark} strokeWidth="3"/><circle cx="71" cy="31" r="12" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <circle cx="29" cy="31" r="6" fill={p.light} opacity="0.5"/><circle cx="71" cy="31" r="6" fill={p.light} opacity="0.5"/>
  <path d="M32 28L50 19L68 28L75 55L63 78L50 95L37 78L25 55Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M32 28L50 19V95L37 78L25 55Z" fill={p.light} opacity="0.42"/>
  <EyePair accent={p.accent} left="M30 45L44 42L41 51L30 52Z" right="M70 45L56 42L59 51L70 52Z"/>
  <path d="M39 61L50 55L61 61L56 79L50 91L44 79Z" fill={p.dark}/><circle cx="50" cy="73" r="3.3" fill="#11161d"/>
  <path d="M42 72L24 68M42 76L22 78M58 72L76 68M58 76L78 78" stroke={p.light} strokeWidth="1.6" strokeLinecap="round"/>
</>;

const Raccoon = ({ p }) => <>
  <circle cx="29" cy="31" r="11" fill={p.mid} stroke={p.dark} strokeWidth="3"/><circle cx="71" cy="31" r="11" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M28 29L50 18L72 29L81 57L68 80L50 90L32 80L19 57Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M28 29L50 18V90L32 80L19 57Z" fill={p.light} opacity="0.38"/>
  <path d="M18 45L43 33L50 44L57 33L82 45L75 62L57 60L50 53L43 60L25 62Z" fill={p.dark}/>
  <path d="M22 47L43 41L47 50L39 57L24 55ZM78 47L57 41L53 50L61 57L76 55Z" fill="#303841"/>
  <EyePair accent={p.accent} left="M25 46L43 43L40 53L25 54Z" right="M75 46L57 43L60 53L75 54Z"/>
  <path d="M38 64L50 57L62 64L57 79L50 84L43 79Z" fill="#20262d"/><path d="M45 65H55L50 73Z" fill="#080b0e"/>
  <path d="M36 79L50 85L64 79" fill="none" stroke={p.light} strokeWidth="2" opacity="0.65"/>
</>;

const Cat = ({ p }) => <>
  <path d="M25 36L17 11L43 29ZM75 36L83 11L57 29Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M27 28L50 18L73 28L82 56L68 79L50 89L32 79L18 56Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M27 28L50 18V89L32 79L18 56Z" fill={p.light} opacity="0.42"/>
  <path d="M19 52L43 37L50 51L31 65ZM81 52L57 37L50 51L69 65Z" fill={p.dark}/>
  <EyePair accent={p.accent} left="M24 47L43 43L39 53L24 54Z" right="M76 47L57 43L61 53L76 54Z"/>
  <path d="M38 64L50 57L62 64L57 78L50 83L43 78Z" fill="#252b32"/><path d="M45 65H55L50 72Z" fill="#090b0e"/><path d="M42 75L34 80M58 75L66 80" stroke={p.light} strokeWidth="1.7"/>
</>;

const Dog = ({ p }) => <>
  <path d="M31 28L11 19L17 73L35 64L39 34ZM69 28L89 19L83 73L65 64L61 34Z" fill={p.dark} stroke="#11161d" strokeWidth="3"/>
  <path d="M29 26L50 17L71 26L80 55L68 79L50 90L32 79L20 55Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M29 26L50 17V90L32 79L20 55Z" fill={p.light} opacity="0.42"/>
  <path d="M22 46L43 35L50 49L33 61ZM78 46L57 35L50 49L67 61Z" fill="#30271c"/>
  <EyePair accent={p.accent} left="M26 44L43 41L40 51L26 52Z" right="M74 44L57 41L60 51L74 52Z"/>
  <path d="M31 58L50 50L69 58L64 81L50 89L36 81Z" fill="#a58e6c"/><path d="M42 62H58L50 72Z" fill="#090b0d"/>
  <path d="M40 77Q50 84 60 77" fill="none" stroke="#4a3b2a" strokeWidth="2"/>
</>;

const Fox = ({ p }) => <>
  <path d="M25 38L12 7L43 27ZM75 38L88 7L57 27Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M27 27L50 17L73 27L83 53L67 76L50 91L33 76L17 53Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M27 27L50 17V91L33 76L17 53Z" fill={p.light} opacity="0.45"/>
  <path d="M18 51L43 34L50 49L29 65ZM82 51L57 34L50 49L71 65Z" fill={p.dark}/>
  <EyePair accent={p.accent} left="M23 45L43 40L39 52L22 53Z" right="M77 45L57 40L61 52L78 53Z"/>
  <path d="M31 61L50 51L69 61L60 79L50 89L40 79Z" fill="#e4d4bd" opacity="0.72"/><path d="M45 62H55L50 70Z" fill="#101215"/>
</>;

const Donkey = ({ p }) => <>
  <path d="M34 37L28 1L44 29ZM66 37L72 1L56 29Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M34 28L50 18L66 28L73 55L64 82L50 94L36 82L27 55Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M34 28L50 18V94L36 82L27 55Z" fill={p.light} opacity="0.42"/>
  <path d="M28 47L44 38L50 50L36 61ZM72 47L56 38L50 50L64 61Z" fill="#252b31"/>
  <EyePair accent={p.accent} left="M31 44L44 42L41 51L31 52Z" right="M69 44L56 42L59 51L69 52Z"/>
  <path d="M35 57L50 51L65 57L62 86L50 93L38 86Z" fill="#aeb3b8"/><path d="M40 68H60L57 84H43Z" fill="#41484f"/>
  <ellipse cx="45" cy="76" rx="2.3" ry="2" fill="#181c20"/><ellipse cx="55" cy="76" rx="2.3" ry="2" fill="#181c20"/>
</>;

const Horse = ({ p }) => <>
  <path d="M35 33L30 10L47 29ZM65 33L70 10L53 29Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M31 28L50 17L69 28L78 55L66 83L50 94L34 83L22 55Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M31 28L50 17V94L34 83L22 55Z" fill={p.light} opacity="0.43"/>
  <path d="M69 30L82 44L73 70L66 55Z" fill={p.dark}/>
  <EyePair accent={p.accent} left="M27 44L43 40L40 51L27 52Z" right="M73 44L57 40L60 51L73 52Z"/>
  <path d="M36 58L50 52L64 58L61 86L50 92L39 86Z" fill="#886449"/><path d="M42 69H58L56 83H44Z" fill="#2a1e17"/>
</>;

const Wolf = ({ p }) => <>
  <path d="M25 39L16 5L43 28ZM75 39L84 5L57 28Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M27 27L50 16L73 27L88 53L79 75L65 79L50 95L35 79L21 75L12 53Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M27 27L50 16V95L35 79L21 75L12 53Z" fill={p.light} opacity="0.42"/>
  {/* Broad predatory cheek ruff */}
  <path d="M13 50L43 32L50 49L30 68L15 72L20 59ZM87 50L57 32L50 49L70 68L85 72L80 59Z" fill={p.dark}/>
  <EyePair accent={p.accent} left="M18 43L43 38L38 52L16 54Z" right="M82 43L57 38L62 52L84 54Z"/>
  <path d="M29 61L50 49L71 61L64 82L50 94L36 82Z" fill="#4a545f"/><path d="M43 63H57L50 73Z" fill="#090b0e"/>
  <path d="M38 79L50 87L62 79" fill="none" stroke="#8d98a4" strokeWidth="2"/>
</>;

const Gorilla = ({ p }) => <>
  <path d="M21 29L36 7H64L79 29L88 55L73 82L50 96L27 82L12 55Z" fill={p.mid} stroke="#11161d" strokeWidth="3"/>
  <path d="M21 29L50 12V96L27 82L12 55Z" fill={p.light} opacity="0.36"/>
  {/* Visible rounded ape ears sit outside the cheek mass */}
  <circle cx="14" cy="49" r="10" fill={p.mid} stroke="#11161d" strokeWidth="3"/><circle cx="86" cy="49" r="10" fill={p.mid} stroke="#11161d" strokeWidth="3"/>
  <circle cx="14" cy="49" r="4" fill={p.light} opacity="0.38"/><circle cx="86" cy="49" r="4" fill={p.light} opacity="0.38"/>
  {/* Wide silverback brow, not an armored visor */}
  <path d="M14 41L42 27L50 42L58 27L86 41L75 58L57 55L50 49L43 55L25 58Z" fill="#171c22"/>
  <EyePair accent={p.accent} left="M27 44L43 40L40 49L28 51Z" right="M73 44L57 40L60 49L72 51Z"/>
  {/* Short projecting muzzle with explicit nostrils */}
  <path d="M24 58L50 47L76 58L71 84L50 94L29 84Z" fill="#3c4650" stroke="#11161d" strokeWidth="2"/>
  <path d="M32 66L50 72L68 66L64 84L50 90L36 84Z" fill="#11161d"/>
  <ellipse cx="42" cy="76" rx="3.5" ry="2.7" fill="#7d8792"/><ellipse cx="58" cy="76" rx="3.5" ry="2.7" fill="#7d8792"/>
  <path d="M39 88Q50 93 61 88" fill="none" stroke="#68737f" strokeWidth="2"/>
</>;

const Lion = ({ p }) => <>
  <path d="M50 5L65 15L80 14L84 31L96 42L88 59L90 76L72 82L62 96H38L28 82L10 76L12 59L4 42L16 31L20 14L35 15Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M28 28L50 17L72 28L80 55L66 78L50 90L34 78L20 55Z" fill={p.light} stroke={p.dark} strokeWidth="3"/>
  <path d="M28 28L50 17V90L34 78L20 55Z" fill={p.mid} opacity="0.56"/>
  <path d="M21 51L43 36L50 50L32 65ZM79 51L57 36L50 50L68 65Z" fill={p.dark}/>
  <EyePair accent={p.accent} left="M25 45L43 41L39 53L24 54Z" right="M75 45L57 41L61 53L76 54Z"/>
  <path d="M34 61L50 53L66 61L61 78L50 85L39 78Z" fill="#d9c08d"/><path d="M44 62H56L50 70Z" fill="#17120d"/>
</>;

const Eagle = ({ p }) => <>
  <path d="M50 17L75 28L92 22L78 49L91 61L64 60L50 80L36 60L9 61L22 49L8 22L25 28Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M27 37L50 20L73 37L66 64L50 82L34 64Z" fill={p.light}/>
  <path d="M27 37L50 20V82L34 64Z" fill={p.mid} opacity="0.62"/>
  <path d="M20 42L44 34L50 48L31 57ZM80 42L56 34L50 48L69 57Z" fill={p.dark}/>
  <EyePair accent={p.accent} left="M23 41L43 38L39 49L22 50Z" right="M77 41L57 38L61 49L78 50Z"/>
  <path d="M42 57L58 57L50 79Z" fill={p.accent} stroke="#6f5314" strokeWidth="2"/>
</>;

const Dragon = ({ p }) => <>
  {/* Swept horns and cheek spikes create a reptilian silhouette */}
  <path d="M32 35L10 7L39 25L28 8L47 22ZM68 35L90 7L61 25L72 8L53 22Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M29 24L50 8L71 24L88 51L79 70L88 82L66 78L50 98L34 78L12 82L21 70L12 51Z" fill={p.mid} stroke={p.dark} strokeWidth="3"/>
  <path d="M29 24L50 8V98L34 78L12 82L21 70L12 51Z" fill={p.light} opacity="0.38"/>
  {/* Reptilian brow plates */}
  <path d="M13 48L43 28L50 47L29 62L15 66ZM87 48L57 28L50 47L71 62L85 66Z" fill={p.dark}/>
  <EyePair accent={p.accent} left="M17 40L43 34L38 51L15 53Z" right="M83 40L57 34L62 51L85 53Z"/>
  {/* Long armored snout */}
  <path d="M27 59L50 46L73 59L68 85L50 97L32 85Z" fill="#26303a" stroke="#0a0d11" strokeWidth="2"/><path d="M35 65L50 71L65 65L61 85L50 92L39 85Z" fill="#090c10"/>
  <path d="M33 76L22 72M67 76L78 72" stroke={p.accent} strokeWidth="2.5" strokeLinecap="round"/>
  <g fill={p.accent} opacity="0.7"><path d="M43 20L50 13L57 20L50 26Z"/><path d="M45 30L50 25L55 30L50 35Z"/></g>
</>;

const ARTWORK = Object.freeze({
  potato: Potato, mouse: Mouse, raccoon: Raccoon, cat: Cat, dog: Dog, fox: Fox,
  donkey: Donkey, horse: Horse, wolf: Wolf, gorilla: Gorilla, lion: Lion,
  eagle: Eagle, dragon: Dragon,
});

export const BadgeTotemArtwork = ({ totemKey, includeShield = true }) => {
  const key = ARTWORK[totemKey] ? totemKey : 'potato';
  const Artwork = ARTWORK[key];
  const palette = PALETTES[key];
  return <g data-totem={key}>{includeShield && <Shield accent="#d6ad3b" />}<Artwork p={palette} /></g>;
};

export const BadgeTotem = ({ rate = 0 }) => {
  const stage = getBadgeTotemStage(rate);
  return <g transform="translate(17 17) scale(0.26)"><BadgeTotemArtwork totemKey={stage.key} /></g>;
};

export const BadgeTotemIcon = ({ totemKey, label, size = 28, decorative = false }) => {
  const stage = BADGE_TOTEM_STAGES.find(item => item.key === totemKey) || BADGE_TOTEM_STAGES[0];
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? 'true' : undefined}
      aria-label={decorative ? undefined : (label || `${stage.label} totem`)}
      className="badge-totem-icon"
    >
      <BadgeTotemArtwork totemKey={stage.key} />
    </svg>
  );
};

export default BadgeTotem;
